import { usePostHog } from "@posthog/react";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type z from "zod";

import { Alert } from "@/components/base/alert";
import { useAppForm } from "@/components/base/form";
import {
  paymentCardSchema,
  useUsaePay,
} from "@/components/base/form/fields/payment-key";
import { title } from "@/components/base/primitives";
import { Cart } from "@/components/registrations/cart";
import {
  registrationPageSchema,
  useCart,
  useCartItems,
  useCartTotal,
} from "@/components/registrations/context";
import { checkoutSchema } from "@/components/registrations/schemas";
import { useTRPC } from "@/integrations/trpc/react";
import { DefaultLayout } from "@/layouts/default";

export const Route = createFileRoute("/account/registrations/checkout")({
  validateSearch: registrationPageSchema,
  head: () => ({
    meta: [{ title: "Checkout" }],
  }),
  scripts: () => [
    {
      src: `${import.meta.env.VITE_USAEPAY_BASE_URL}/js/v2/pay.js`,
    },
  ],
  component: RouteComponent,
});

const schema = checkoutSchema
  .omit({
    cart: true,
    paymentKey: true,
  })
  .extend({
    paymentCard: paymentCardSchema,
  });

// Test card
//
// https://help.usaepay.info/developer/reference/testcards/
//
// "creditcard": {
//     "cardholder": "John Doe",
//     "number": "4000100011112224",
//     "expiration": "0919",
//     "cvc": "123",
//     "avs_street": "1234 Main",
//     "avs_zip": "12345"
// }

function RouteComponent() {
  const search = Route.useSearch();

  const cart = useCart("checkout");
  const items = useCartItems("checkout");
  const total = useCartTotal("checkout");

  const { getPaymentKey, paymentCard } = useUsaePay();

  const navigate = useNavigate();
  const posthog = usePostHog();

  const trpc = useTRPC();
  const {
    mutate: checkout,
    isPending,
    failureReason,
  } = useMutation({
    ...trpc.payments.checkout.mutationOptions(),
    onSuccess: (data) => {
      if (data.paymentIntentId) {
        navigate({
          to: "/account/registrations/complete/$paymentIntentId",
          params: { paymentIntentId: String(data.paymentIntentId) },
        });
      } else if (data.status === "completed" && data.invoiceId) {
        navigate({
          to: "/account/registrations/success",
          search: { invoiceId: data.invoiceId },
        });
      }
    },
  });

  const form = useAppForm({
    defaultValues: {
      billingInformation: {
        firstName: "",
        lastName: "",
        address: ["", ""],
        city: "",
        state: "",
        postalCode: "",
      },
      paymentCard: {
        cnum: false,
        cvv: false,
        exp: false,
      },
      emailOptIn: false,
    } as z.infer<typeof schema>,
    validators: {
      onChange: schema,
    },
    onSubmit: async ({
      value: { billingInformation, emailOptIn },
      formApi,
    }) => {
      if (!formApi.state.isValid) {
        return;
      }

      // Get payment key from USAePay here
      const result = await getPaymentKey();

      if (result.error) {
        return {
          form: "Invalid card information.",
        };
      }

      posthog.capture("checkout_started", {
        membership_count: cart.memberships.length,
        team_count: cart.teams.length,
        total,
      });

      checkout({
        billingInformation,
        paymentKey: result.key,
        emailOptIn: emailOptIn || undefined,
        cart: {
          memberships: cart.memberships,
          teams: cart.teams.map(({ divisionId, profileIds }) => ({
            divisionId,
            profileIds,
          })),
        },
      });
    },
  });

  if (items.length === 0) {
    return (
      <DefaultLayout>
        <h1 className={title({ className: "text-center" })}>Checkout</h1>

        <div className="mx-auto max-w-md text-center">
          <p className="text-default-600 text-lg">Your cart is empty.</p>
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout>
      <h1 className={title({ className: "text-center" })}>Checkout</h1>

      <div className="mx-auto grid w-full max-w-2xl grid-cols-1 items-start gap-x-4 gap-y-4 md:grid-cols-8">
        <form
          className="order-2 col-span-full flex flex-col gap-4 md:order-1 md:col-span-5"
          onSubmit={(e) => {
            e.preventDefault();

            form.handleSubmit();
          }}
        >
          <div className="grid grid-cols-6 items-start gap-3 rounded-lg bg-white p-4 pb-6">
            <h2 className={title({ size: "xs", class: "mb-2 col-span-full" })}>
              Billing Information
            </h2>

            <form.AppField name="billingInformation.firstName">
              {(field) => (
                <field.Text
                  className="col-span-3"
                  field={field}
                  label="First Name"
                  isRequired={true}
                />
              )}
            </form.AppField>

            <form.AppField name="billingInformation.lastName">
              {(field) => (
                <field.Text
                  className="col-span-3"
                  field={field}
                  label="Last Name"
                  isRequired={true}
                />
              )}
            </form.AppField>

            <form.AppField name="billingInformation.address" mode="array">
              {(field) => (
                <>
                  <form.AppField name="billingInformation.address[0]">
                    {(subField) => (
                      <field.Text
                        className="col-span-full"
                        field={subField}
                        label="Address"
                        isRequired={true}
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="billingInformation.address[1]">
                    {(subField) => (
                      <field.Text
                        className="col-span-full"
                        field={subField}
                        label="Apartment, suite, etc"
                      />
                    )}
                  </form.AppField>
                </>
              )}
            </form.AppField>

            <form.AppField name="billingInformation.city">
              {(field) => (
                <field.Text
                  className="col-span-full"
                  field={field}
                  label="City"
                  isRequired={true}
                />
              )}
            </form.AppField>

            <form.AppField name="billingInformation.state">
              {(field) => (
                <field.Text
                  className="col-span-3"
                  field={field}
                  label="State"
                  isRequired={true}
                />
              )}
            </form.AppField>

            <form.AppField name="billingInformation.postalCode">
              {(field) => (
                <field.Text
                  className="col-span-3"
                  field={field}
                  label="Postal Code"
                  isRequired={true}
                />
              )}
            </form.AppField>
          </div>

          <form.AppField name="paymentCard">
            {(field) => (
              <field.PaymentKey
                className="rounded-lg bg-white p-4 pb-0"
                field={field}
                label="Billing Information"
                paymentCard={paymentCard}
              />
            )}
          </form.AppField>

          <form.AppField name="emailOptIn">
            {(field) => (
              <field.Checkbox
                label="I would like to receive periodic updates from the CBVA"
                field={field}
              />
            )}
          </form.AppField>

          {failureReason && (
            <Alert
              title="Payment Failed"
              description={
                failureReason.message ||
                "Your payment could not be processed. Please check your card details and try again."
              }
            />
          )}

          <form.AppForm>
            <form.SubmitButton
              isDisabled={isPending}
              size="lg"
              radius="full"
              allowInvalid={true}
            >
              Pay ${total}
            </form.SubmitButton>
          </form.AppForm>

          <p className="text-center text-xs text-gray-500">
            American Express is not accepted.
          </p>

          {cart.teams.length > 0 && (
            <p className="text-center text-xs text-gray-500">
              Tournament spots are confirmed on a first-come, first-served basis
              and are not guaranteed until your registration is finalized. If a
              spot is no longer available, your card will not be charged.
            </p>
          )}

          {cart.teams.length > 0 && (
            <p className="text-center text-xs text-gray-500">
              View our{" "}
              <a
                href="/faqs/tournaments?faq=Is+there+a+fee+for+refunding+an+entry%3F"
                className="text-primary underline"
                target="_blank"
                rel="noreferrer"
              >
                Refund Policy
              </a>
              {" and "}
              <a
                href="/faqs/waitlist"
                className="text-primary underline"
                target="_blank"
                rel="noreferrer"
              >
                Waitlist FAQs
              </a>
              .
            </p>
          )}
        </form>

        <Cart
          route="checkout"
          className="order-1 col-span-full md:order-2 md:col-span-3"
        />
      </div>
    </DefaultLayout>
  );
}
